import { ButtonProps } from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import { TooltipProps } from "@mui/material/Tooltip";
import { useGridApiContext, useGridRootProps } from "@mui/x-data-grid-premium";
import PropTypes from "prop-types";
import { cloneElement, forwardRef, useContext } from "react";
import { DataGridContext } from "../../context/DataGridContext";
import { GridAutosizeColumnsOptions } from "../../models/gridAutosizeColumns";
import { GridHeaderFiltersOptions } from "../../models/gridHeaderFilters";
import { GridToolbarOptionsContainer } from "./GridToolbarOptionsContainer";
import { GridResetStateOptions } from "../../models/gridResetState";
import { useResetGridState } from "../../hooks/useResetGridState";
import { DataGridProps } from "@components/DataGrid/models/dataGridProps";
import { Divider, ListItem, ListItemIcon, ListItemText } from "@mui/material";
import Check from "@mui/icons-material/Check";

export interface GridOptionsDisplayOptions {
  /**
   * If `true`, this export option will be removed from the GridToolbarOptions menu.
   * @default false
   */
  disableToolbarButton?: boolean;
}

export interface GridOptionsMenuItemProps<Options extends {}> {
  hideMenu?: () => void;
  options?: Options & GridOptionsDisplayOptions;
}

export type GridAutosizeColumnsOptionsMenuItemProps = GridOptionsMenuItemProps<GridAutosizeColumnsOptions>;

export type GridHeaderFiltersOptionsMenuItemProps = GridOptionsMenuItemProps<GridHeaderFiltersOptions>;

export type GridResetStateOptionsMenuItemProps = GridOptionsMenuItemProps<GridResetStateOptions>;

export interface GridToolbarOptionsButtonMenuProps {
  autosizeColumnsOptions?: GridAutosizeColumnsOptions & GridOptionsDisplayOptions;
  headerFiltersOptions?: GridHeaderFiltersOptions & GridOptionsDisplayOptions;
  resetGridStateOptions?: GridResetStateOptions & GridOptionsDisplayOptions;
  /**
   * The props used for each slot inside.
   * @default {}
   */
  slotProps?: { button?: Partial<ButtonProps>; tooltip?: Partial<TooltipProps> };
  [key: string]: any; // TODO: what the heck is this?
}

export const GridAutosizeColumnsOptionsMenuItem = (props: GridAutosizeColumnsOptionsMenuItemProps) => {
  const apiRef = useGridApiContext();
  const dataGridContext = useContext(DataGridContext);
  const { hideMenu, options, ...other } = props;

  const handleAutosizeColumns = () => {
    const newAutosizeColumns = dataGridContext?.autosizeColumns ? false : true;
    dataGridContext?.setAutosizeColumns(newAutosizeColumns);
    const newAutosizeTreeDataGroupingColumns = dataGridContext?.autosizeTreeDataGroupingColumns ? false : true;
    dataGridContext?.setAutosizeTreeDataGroupingColumns(newAutosizeTreeDataGroupingColumns);
    if (newAutosizeColumns) {
      setTimeout(() => {
        apiRef.current.autosizeColumns({
          includeOutliers: true,
          includeHeaders: true,
          expand: true,
        });
      }, 0);
    }
    hideMenu?.();
  };

  return (
    <MenuItem onClick={handleAutosizeColumns} {...other}>
      <ListItemIcon>{dataGridContext?.autosizeColumns && <Check />}</ListItemIcon>
      <ListItemText>{apiRef.current.getLocaleText("toolbarOptionsAutosizeColumns" as any)}</ListItemText>
    </MenuItem>
  );
};

export const GridHeaderFiltersOptionsMenuItem = (props: GridHeaderFiltersOptionsMenuItemProps) => {
  const apiRef = useGridApiContext();
  const dataGridContext = useContext(DataGridContext);
  const { hideMenu, options, ...other } = props;

  const handleHeaderFilters = () => {
    dataGridContext?.setHeaderFilters(dataGridContext?.headerFilters ? false : true);
    hideMenu?.();
  };

  return (
    <MenuItem onClick={handleHeaderFilters} {...other}>
      <ListItemIcon>{dataGridContext?.headerFilters && <Check />}</ListItemIcon>
      <ListItemText>{apiRef.current.getLocaleText("toolbarOptionsHeaderFilters" as any)}</ListItemText>
    </MenuItem>
  );
};

export function GridResetStateOptionsMenuItem(props: GridResetStateOptionsMenuItemProps) {
  const apiRef = useGridApiContext();
  const dataGridContext = useContext(DataGridContext);
  const { hideMenu, options, ...other } = props;

  const resetState = useResetGridState(apiRef);
  const handleResetState = () => {
    resetState();
    hideMenu?.();
  };

  return (
    <>
      <Divider />
      <MenuItem onClick={handleResetState} {...other}>
        <ListItemIcon></ListItemIcon>
        <ListItemText>{apiRef.current?.getLocaleText("toolbarOptionsResetGridState" as any)}</ListItemText>
      </MenuItem>
    </>
  );
}

const GridToolbarOptionsButtonMenu = forwardRef<HTMLButtonElement, GridToolbarOptionsButtonMenuProps>(function GridToolbarOptions(props, ref) {
  const { autosizeColumnsOptions = {}, headerFiltersOptions = {}, resetGridStateOptions = {}, ...other } = props;
  const apiRef = useGridApiContext();

  const preProcessedButtons = apiRef.current
    .unstable_applyPipeProcessors("optionsMenu" as any, [], { autosizeColumnsOptions, headerFiltersOptions, resetGridStateOptions })
    .sort((a: any, b: any) => (a.componentName > b.componentName ? 1 : -1));

  if (preProcessedButtons.length === 0) {
    return null;
  }

  return (
    <GridToolbarOptionsContainer {...other} ref={ref} slotProps={{ button: { size: "small" } }}>
      {preProcessedButtons.map((button: any, index: number) => cloneElement(button.component, { key: index }))}
    </GridToolbarOptionsContainer>
  );
});

GridToolbarOptionsButtonMenu.propTypes = {
  // ----------------------------- Warning --------------------------------
  // | These PropTypes are generated from the TypeScript type definitions |
  // | To update them edit the TypeScript types and run "pnpm proptypes"  |
  // ----------------------------------------------------------------------
  autosizeColumnsOptions: PropTypes.object,
  headerFiltersOptions: PropTypes.object,
  resetGridStateOptions: PropTypes.object,
  /**
   * The props used for each slot inside.
   * @default {}
   */
  slotProps: PropTypes.object,
} as any;

export { GridToolbarOptionsButtonMenu };
